import React, { useState } from 'react';
import { Card, CardContent, Typography, CardActions, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Exercise } from '../../types/interfaces'; // Import the Exercise type
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'; // Import an icon for active/inactive status
import {
  Quiz,
  Edit,
  School,
  AutoStories,
  Calculate,
  Functions,
  Link,
  Sort,
  FormatListNumbered,
} from '@mui/icons-material';

// Function to get exercise type icon
const getExerciseTypeIcon = (exerciseType: string) => {
  const iconProps = { sx: { fontSize: 24, color: '#3f51b5', mr: 1 } };
  
  switch (exerciseType) {
    case 'fill_in_the_blanks':
      return <Edit {...iconProps} />;
    case 'material_based':
    case 'material_based_ai_assisted':
      return <AutoStories {...iconProps} />;
    case 'material_based_multichoice':
    case 'reading_comprehension_multichoice':
    case 'true_false':
      return <Quiz {...iconProps} />;
    case 'pairing':
      return <Link {...iconProps} />;
    case 'sort_to_boxes':
      return <Sort {...iconProps} />;
    case 'math_arit_2operand_1phase':
    case 'math_arit_2operand_2phase':
      return <Calculate {...iconProps} />;
    case 'math_arit_rounding_precision':
      return <Functions {...iconProps} />;
    case 'ordering':
      return <FormatListNumbered {...iconProps} />;
    default:
      return <School {...iconProps} />;
  }
};

interface ExerciseItemProps {
  exercise: Exercise;
  onDelete: (id: number) => void;
  courseId: string; // Add courseId as a prop
}

const ExerciseItem: React.FC<ExerciseItemProps> = ({ exercise, onDelete, courseId }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleDelete = async () => {
    await onDelete(exercise.id);
    setOpen(false);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCardClick = () => {
    navigate(`/course/${courseId}/lesson/${exercise.lessonId}/exercise/${exercise.id}`);
  };

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick();
    }
  };

  const stopCardClickPropagation = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <Card
      sx={{ cursor: 'pointer' }}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
        {/* Order Number */}
        <Typography variant="h3" component="div" sx={{ mr: 3 }}>
          {exercise.order}
        </Typography>

        {/* Exercise Details */}
        <Box sx={{ flexGrow: 1 }}>
          {/* Exercise Title/Instructions */}
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
            {exercise.instructions || `Harjoitus ${exercise.order}`}
          </Typography>
          
          {/* Exercise Type */}
          <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
            {getExerciseTypeIcon(exercise.type)}
            <Typography variant="body2" color="text.secondary">
              Tyyppi: {exercise.type}
            </Typography>
          </Box>
          
          {/* Additional Info */}
          {exercise.additionalInfo && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {exercise.additionalInfo}
            </Typography>
          )}
          
          {/* Max Score */}
          <Typography variant="body2" color="text.secondary">
            Maksimipisteet: {exercise.maxScore}
          </Typography>
        </Box>

        {/* Active/Inactive Indicator */}
        <FiberManualRecordIcon 
          sx={{ color: exercise.maxScore > 0 ? 'green' : 'gray' }} 
          titleAccess={exercise.maxScore > 0 ? 'Aktiivinen' : 'Ei aktiivinen'}
        />
      </CardContent>
      <CardActions>
        {/* Use courseId in the RouterLink paths */}
        <Button size="small" component={RouterLink} to={`/course/${courseId}/lesson/${exercise.lessonId}/exercise/${exercise.id}`} onClick={stopCardClickPropagation}>
          Näytä
        </Button>
        <Button size="small" component={RouterLink} to={`/course/${courseId}/lesson/${exercise.lessonId}/exercise/edit/${exercise.id}`} onClick={stopCardClickPropagation}>
          Muokkaa
        </Button>
        {/* Add the Assign button */}
        <Button size="small" component={RouterLink} to={`/course/${courseId}/lesson/${exercise.lessonId}/exercise/assign/${exercise.id}`} onClick={stopCardClickPropagation}>
          Määrää tehtävä
        </Button>
        <Button size="small" color="secondary" onClick={(event) => {
          event.stopPropagation();
          handleClickOpen();
        }}>
          Poista
        </Button>
      </CardActions>

      <Dialog
        open={open}
        onClose={handleClose}
        onClick={stopCardClickPropagation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Vahvista poisto"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Oletko varma että haluat poistaa harjoituksen {exercise.order}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Peruuta
          </Button>
          <Button onClick={handleDelete} color="secondary" autoFocus>
            Poista
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ExerciseItem;
